import { css } from '@emotion/react';
import { t } from 'i18n';
import { BoundConnection } from 'models';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { DeviceType, useMaxDeviceWidth } from '../hooks';
import { getFlightDate } from '../utils';

import { Divider } from './Divider';
import { ErrorMessage } from './ErrorMessage';
import { theme } from './theme';

const styles = {
  wrapper: css`
    background-color: ${theme.colors.white};
    border-radius: ${theme.borders.radius.lg};
    min-height: 90px;
    padding: ${theme.spacing.lg};
    position: static;
    z-index: 2;

    @media (max-width: ${theme.breakpoints.laptop}) {
      border-top: ${theme.borders.widths.sm} ${theme.colors.gray[1]} solid;
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
      padding: ${theme.spacing.md};
      position: fixed;
      width: 100%;
      bottom: 0;
      left: 0;
    }
  `,
  content: (notSelected: boolean) => css`
    @media (max-width: ${theme.breakpoints.laptop}) {
      display: flex;
      align-items: center;
      justify-content: ${notSelected ? 'center' : 'space-between'};
    }
  `,
  title: (boundsSelected: boolean) => css`
    margin-bottom: 0;

    @media (max-width: ${theme.breakpoints.laptop}) {
      margin-bottom: ${theme.spacing.xs};
      font-size: ${theme.fontSize.md};
      display: ${boundsSelected ? 'block' : 'none'};
    }
  `,
  textWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.md};

    @media (max-width: ${theme.breakpoints.laptop}) {
      margin-bottom: 0;
      gap: ${theme.spacing.xs};
    }
  `,
  bound: css`
    display: flex;
    gap: ${theme.spacing.xs};
    align-items: center;

    @media (max-width: ${theme.breakpoints.laptop}) {
      font-size: ${theme.fontSize.sm};
    }

    @media (max-width: ${theme.breakpoints.mobileM}) {
      flex-direction: column;
      align-items: flex-start;
    }

    & > h4 {
      font-size: ${theme.fontSize.md};
      margin-bottom: 0;

      @media (max-width: ${theme.breakpoints.laptop}) {
        font-size: ${theme.fontSize.sm};
      }
    }
  `,
  divider: css`
    display: block;

    @media (max-width: ${theme.breakpoints.laptop}) {
      display: none;
    }
  `,
  errorMessage: css`
    color: ${theme.colors.danger};
    width: fit-content;
    margin: ${theme.spacing.md} 0 0;

    & > p {
      font-size: ${theme.fontSize.sm};
    }

    @media (max-width: ${theme.breakpoints.laptop}) {
      margin: ${theme.spacing.xs} auto 0;
    }
  `,
};

type Props = {
  reservation: { outbound: BoundConnection; inbound?: BoundConnection };
  outboundSelected: boolean;
  inboundSelected: boolean;
  handleClick: VoidFunction;
  handleTracking?: VoidFunction;
};

export const SelectionSummary = ({
  reservation,
  outboundSelected,
  inboundSelected,
  handleClick,
  handleTracking,
}: Props) => {
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const outbound = outboundSelected ? reservation?.outbound : undefined;
  const inbound = inboundSelected ? reservation?.inbound : undefined;

  const outboundSelectedText = outbound ? t('common:outBound') : '';
  const inboundSelectedText = inbound ? t('common:inBound') : '';

  const isAnyBoundSelected = outboundSelected || inboundSelected;

  const buttonText = isAnyBoundSelected ? t('common:continue') : t('noFlightsSelected');

  useEffect(() => {
    setShowErrorMessage(false);
  }, [isAnyBoundSelected, setShowErrorMessage]);

  const handleButtonClick = () => {
    handleTracking?.();

    if (!isAnyBoundSelected) {
      setShowErrorMessage(true);
    } else {
      handleClick();
    }
  };

  return (
    <div css={styles.wrapper}>
      <div css={styles.content(!isAnyBoundSelected)}>
        <div css={styles.textWrapper}>
          <h3 css={styles.title(isAnyBoundSelected)}>{t('rebooking.flightSelect.summaryTitle')}</h3>
          <Divider css={styles.divider} />
          {(inbound || outbound) && (
            <>
              {outbound && (
                <div css={styles.bound}>
                  <h4>{outboundSelectedText}</h4>
                  <span>{getFlightDate(outbound.startTimeLocal)}</span>
                </div>
              )}
              {inbound && (
                <div css={styles.bound}>
                  <h4>{inboundSelectedText}</h4>
                  <span>{getFlightDate(inbound.startTimeLocal)}</span>
                </div>
              )}
            </>
          )}
        </div>
        <button onClick={handleButtonClick}>{buttonText}</button>
      </div>
      {showErrorMessage && (
        <ErrorMessage message={t('rebooking.flightSelect.nonSelectedWarning')} css={styles.errorMessage} />
      )}
    </div>
  );
};

export const SelectionSummarySkeleton = () => {
  const isSmall = useMaxDeviceWidth(DeviceType.Laptop);

  return (
    <div
      css={[
        styles.wrapper,
        css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing.md};
          padding: 1.9rem ${theme.spacing.xl};
        `,
      ]}
    >
      {!isSmall && (
        <>
          <Skeleton height={24} borderRadius={24} />
          <Divider />
        </>
      )}
      <Skeleton height={48} borderRadius={48} />
    </div>
  );
};
